import React, { useState, lazy, Suspense, useRef, useEffect } from 'react';
import { Shield, AlertCircle } from 'lucide-react';
import { LoadingSpinner } from './ui';
import { useReCaptchaContext } from '../contexts/ReCaptchaContext';

const GoogleReCaptchaProviderLazy = lazy(() => 
  import('react-google-recaptcha-v3').then(module => ({
    default: module.GoogleReCaptchaProvider
  }))
);

interface ReCaptchaProviderProps {
  children: React.ReactNode;
}

export default function ReCaptchaProvider({ children }: ReCaptchaProviderProps) {
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const maxRetries = 3;
  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
  const containerRef = useRef<HTMLDivElement>(null);
  const { isEnabled, isLoading: isConfigLoading } = useReCaptchaContext();

  useEffect(() => {
    console.log('ReCaptchaProvider state:', {
      isEnabled,
      isConfigLoading,
      isScriptLoaded,
      retryCount,
      error,
      siteKey
    });
  }, [isEnabled, isConfigLoading, isScriptLoaded, retryCount, error, siteKey]);

  // Show loading state while config is being fetched
  if (isConfigLoading) {
    console.log('ReCaptcha config is loading...');
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // If reCAPTCHA is disabled, render children directly without the provider
  if (!isEnabled) {
    console.log('ReCaptcha is disabled, rendering children directly');
    return <>{children}</>;
  }

  if (!siteKey) {
    console.error('ReCaptcha site key is missing');
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
          <div className="flex flex-col items-center text-center">
            <div className="bg-red-100 p-3 rounded-full mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Configuration Error</h1>
            <p className="text-gray-600">
              reCAPTCHA is not properly configured. Please check your environment variables.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!/^[A-Za-z0-9_-]+$/.test(siteKey)) {
    console.error('Invalid ReCaptcha site key format:', siteKey);
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
          <div className="flex flex-col items-center text-center">
            <div className="bg-red-100 p-3 rounded-full mb-4">
              <Shield className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Invalid Configuration</h1>
            <p className="text-gray-600">
              The reCAPTCHA site key appears to be invalid. Please check your configuration.
            </p>
          </div>
        </div>
      </div>
    );
  }

  console.log('Initializing ReCaptcha with site key:', siteKey);

  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      }
    >
      <div ref={containerRef} className="contents">
        <GoogleReCaptchaProviderLazy
          reCaptchaKey={siteKey}
          scriptProps={{
            async: true,
            defer: true,
            appendTo: 'head',
            nonce: undefined,
            id: 'recaptcha-script',
            onError: (error) => {
              console.error('ReCaptcha script error:', error);
              if (retryCount < maxRetries) {
                console.log(`Retrying ReCaptcha initialization (attempt ${retryCount + 1}/${maxRetries})`);
                setRetryCount(prev => prev + 1);
                const delay = Math.pow(2, retryCount + 1) * 2000;
                setTimeout(() => {
                  setIsScriptLoaded(false);
                  window.location.reload();
                }, delay);
              } else {
                console.error('Max retries reached for ReCaptcha initialization');
                setError('Failed to load reCAPTCHA. Please check your internet connection and try again.');
              }
            },
            onLoad: () => {
              console.log('ReCaptcha script loaded successfully');
              setRetryCount(0);
              setError(null);
              setIsScriptLoaded(true);
            }
          }}
          container={{
            parameters: {
              badge: 'bottomright',
              size: 'invisible',
              theme: 'light'
            }
          }}
          language="en"
          useEnterprise={false}
          useRecaptchaNet={true}
          scriptSource="https://www.recaptcha.net/recaptcha/api.js"
          onVerify={(token) => {
            console.log('ReCaptcha verification completed');
          }}
        >
          {children}
        </GoogleReCaptchaProviderLazy>
      </div>
    </Suspense>
  );
}